import { Service } from "../Service/Service";
import { Tag, registerCreateByType } from "./Tag";
import { Container } from "./Container/Container";
import { FlexContainer } from "./Container/FlexContainer";
import { CFlexContainer } from "./Container/CFlexContainer";
import { Div } from "./Div";
import { Span } from "./Span";
import { P } from "./P";
import { Img } from "./Img";
import { SVG } from "./SVG";
import { Anchor } from "./Anchor";
import { Reference } from "./Reference";
import { Table } from "./Table";
import { Dynamic } from "./Dynamic";
import { Module } from "./Module";
import { Action } from "./Action";
import { Twitter } from "./Twitter";
import { Youtube } from "./Youtube";
import { Page } from "./Page";
import { Code } from "./Code";
import { Cite } from "./Cite";
import { Menu as MenuTag } from "./App/Menu";
import { ResultListPage } from "./Page/ResultListPage";
import { List } from "./Container/List/List";
import { UImg } from "./Image/UImg";
import { Pdf } from "./Pdf/Pdf";
import { Window as HaWindow } from "./Container/Window/Window";
import { Carousel } from "./Container/List/Carousel";
import { Slider } from "./Container/List/Slider";
import { Pane } from "./Box/Pane";
import { MenuPane } from "./Menu/MenuPane";
import { GMap } from "./Box/GMap";
import { Gallery } from "./Media/Gallery";
import { FormItemRegistry } from "./Form/FormItemRegistry";
import { Form as HaForm } from "./Form/Form";
import { FormController } from "../Controller/Form/FormController";
import { Input as FormInput } from "./Form/Input";
import { Textarea as FormTextarea } from "./Form/Textarea";
import { Navigation as HaNavigation } from "./Navigation/Navigation";
import { log2, getAppService } from "../../../HA";

type TagFactory = (data: Map<any, any>, main: Tag, translation?: any) => void;

export class HtmlService extends Service {

    private static tagRegistry: Map<string, TagFactory> | null = null;

    public static uuidv4(): string {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c: string) => {
            const r = Math.random() * 16 | 0;
            const v = c === "x" ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    public static registerTag(type: string, factory: TagFactory): void {
        HtmlService.getTagRegistry().set(type, factory);
    }

    public static createHtml(data: Map<any, any>, service?: () => void) {
        const t = data.get("t") || "";
        switch (t) {
            case "m":
                data.set("services", service);
                return HtmlService.createMenuApp(data);
            case "pa-rlp":
                return new ResultListPage(data);
            case "pa":
                return new Page(data);
            default:
                return HtmlService.createContainer(data);
        }
    }

    public static createFormItem(data: Map<any, any>) {
        return FormItemRegistry.createTag(data.get("ft") || "", data);
    }

    public static createByType(data: Map<any, any>, main: Tag, translation?: any): void {
        const t = data.get("t");
        const factory = HtmlService.getTagRegistry().get(t);
        if (!factory) {
            return;
        }
        try {
            factory(data, main, translation);
        } catch (e) {
            console.error(e);
            log2(data);
        }
    }

    public static createMenuApp(data: Map<any, any>) {
        return new MenuTag(data);
    }

    public static createContainer(data: Map<any, any>) {
        return new Container(data);
    }

    private static getTagRegistry(): Map<string, TagFactory> {
        if (!HtmlService.tagRegistry) {
            HtmlService.tagRegistry = HtmlService.buildTagRegistry();
        }
        return HtmlService.tagRegistry;
    }

    private static buildTagRegistry(): Map<string, TagFactory> {
        const r = new Map<string, TagFactory>();
        const simple = (Ctor: any) => (d: Map<any, any>, m: Tag) => m.appendChild(new Ctor(d));
        const spanCls = (cls: string) => (d: Map<any, any>, m: Tag) => { const s = new Span(d); s.addClass(cls); m.appendChild(s); };
        const semTag = (tn: string) => (d: Map<any, any>, m: Tag) => { if (!d.get("_tn")) { d.set("_tn", tn); } m.appendChild(new Tag(d)); };

        for (const tn of ["t1", "t2", "t3", "t4"]) { r.set(tn, spanCls(tn)); }
        r.set("tf", spanCls("tf"));

        for (const tn of ["h1", "h2", "h3", "h4"]) { r.set(tn, semTag(tn)); }

        const imgFactory: TagFactory = (d, m, tr) => {
            const src: string = m.translate(d.get("src"), tr);
            if (src) { d.set("src", src); }
            m.appendChild(new Img(d));
        };
        r.set("img", imgFactory);
        r.set("media", imgFactory);

        r.set("cfc", simple(CFlexContainer));
        r.set("fc", simple(FlexContainer));
        r.set("div", simple(Div));
        r.set("c", simple(Div));
        r.set("rl", simple(Div));

        r.set("a", simple(Anchor));
        r.set("span", simple(Span));
        r.set("svg", simple(SVG));
        r.set("b", semTag("b"));
        r.set("i", semTag("i"));
        r.set("t", (d, m) => {
            const tn = d.get("_tn");
            if (tn && SVG.SVG_TAGS.has(tn)) {
                m.appendChild(new SVG(d));
            } else {
                m.appendChild(new Tag(d));
            }
        });
        r.set("txt", (d, m) => m.getElement().appendChild(document.createTextNode(d.get("v") ?? "")));
        r.set("ai", (d) => getAppService().processAction(d.get("d")));

        for (const tn of ["ul", "ol", "li", "main", "header", "footer", "article", "aside",
                           "small", "strong", "em", "u", "mark", "pre", "blockquote",
                           "hr", "br", "label", "button"]) {
            r.set(tn, semTag(tn));
        }

        r.set("p", simple(P));
        r.set("pn", simple(Pane));
        r.set("ref", simple(Reference));
        r.set("cite", simple(Cite));
        r.set("section", semTag("section"));
        r.set("table", simple(Table));

        r.set("formi", (d, m) => m.appendChild(HtmlService.createFormItem(d)));
        r.set("form", (d, m) => {
            const form = new HaForm(d);
            form.setAttribute("i", 1);
            const fd = new Map();
            fd.set("tag", form);
            new FormController(fd);
            m.appendChild(form);
        });
        r.set("input", (d, m) => m.appendChild(new FormInput(d)));
        r.set("inputp", (d, m) => { const inp = new FormInput(d); inp.setAttribute("type", "password"); m.appendChild(inp); });
        r.set("inpute", (d, m) => { const inp = new FormInput(d); inp.setAttribute("type", "email"); m.appendChild(inp); });
        r.set("textarea", simple(FormTextarea));
        r.set("submit", (d, m) => { const inp = new FormInput(d); inp.setAttribute("type", "submit"); m.appendChild(inp); });

        r.set("nav", simple(HaNavigation));

        r.set("dyn", simple(Dynamic));
        r.set("mod", simple(Module));
        r.set("l", simple(List));
        r.set("list", simple(List));
        r.set("uimg", simple(UImg));
        r.set("pdf", simple(Pdf));
        r.set("win", simple(HaWindow));
        r.set("pa", simple(Page));
        r.set("carousel", simple(Carousel));
        r.set("slider", simple(Slider));
        r.set("pa-rlp", simple(ResultListPage));
        r.set("mp", (d, m) => m.appendChild(new MenuPane(d)));
        r.set("map", (d, m) => m.appendChild(new GMap(d)));
        r.set("gal", (d, m) => m.appendChild(new Gallery(d)));
        r.set("code", simple(Code));
        r.set("action", (d) => new Action(d));

        r.set("youtube", (d, m) => {
            const c = new Div();
            c.appendChild(new Youtube(d));
            c.addClass("youtubeContainer");
            m.appendChild(c);
        });
        r.set("twitter", (d, m) => {
            const c = new Div();
            c.appendChild(new Twitter(d));
            c.addClass("twitterContainer");
            m.appendChild(c);
        });

        return r;
    }
}

// Wire Tag.createByType → HtmlService.createByType (slot anti-TDZ ha/442).
registerCreateByType((d, m, t) => HtmlService.createByType(d, m, t));
